import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Award,
  Check,
  X,
  ExternalLink,
  ClipboardCheck,
  Clock,
  FileText,
  Mail,
  BookOpen,
  MessageSquare,
  HelpCircle,
  MessagesSquare,
} from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { key: "pending", label: "Pending", icon: Clock, tone: "clay" },
  { key: "more_info_requested", label: "More info", icon: HelpCircle, tone: "clay" },
  { key: "granted", label: "Approved", icon: Check, tone: "forest" },
  { key: "rejected", label: "Rejected", icon: X, tone: "muted" },
];

const STATUS_STYLES = {
  pending: {
    label: "Pending",
    className:
      "bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/30",
  },
  more_info_requested: {
    label: "More info requested",
    className:
      "bg-[hsl(var(--sb-clay))]/12 text-[hsl(var(--sb-clay))] border-[hsl(var(--sb-clay))]/30",
  },
  granted: {
    label: "Approved",
    className:
      "bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest))]/30",
  },
  rejected: {
    label: "Rejected",
    className: "bg-muted text-muted-foreground border-border",
  },
};

const MANUSCRIPT_LABELS = {
  none: "None yet",
  "1-5": "1–5",
  "6-20": "6–20",
  "20+": "20+",
};

export default function AdminCertifications() {
  const { user } = useAuth();
  const canReview =
    (user?.roles || []).includes("admin") || (user?.roles || []).includes("moderator");

  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadStats = async () => {
    try {
      const { data } = await api.get("/certifications/admin/stats");
      setStats(data);
    } catch (e) {
      /* stats are non-critical */
    }
  };

  const loadList = async (status = tab) => {
    setLoading(true);
    try {
      const { data } = await api.get("/certifications/admin/list", { params: { status } });
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canReview) return;
    loadStats();
    loadList(tab);
    // eslint-disable-next-line
  }, [canReview, tab]);

  const decide = async (cert, action, extra) => {
    try {
      let path;
      let body;
      let successLabel;
      const applicantName = cert.applicant?.display_name || "Applicant";
      if (action === "request-info") {
        path = `/certifications/${cert.id}/request-info`;
        body = { message: extra?.message || "", internal_notes: extra?.internal_notes || "" };
        successLabel = `Requested more info from ${applicantName}.`;
      } else {
        path = `/certifications/${cert.id}/${action}`;
        body = { internal_notes: extra?.internal_notes || "" };
        successLabel =
          action === "grant"
            ? `${applicantName} is now Last Bookmark Certified.`
            : `Application from ${applicantName} was declined.`;
      }
      const { data } = await api.post(path, body);
      toast.success(successLabel);
      // Optimistically remove from current list — it belongs to a different tab now.
      setItems((prev) => prev.filter((c) => c.id !== cert.id));
      loadStats();
      return data;
    } catch (e) {
      toast.error(formatApiError(e));
      throw e;
    }
  };

  if (!canReview) {
    return (
      <SiteShell>
        <div className="container-page py-16 text-center text-muted-foreground">
          Admins or moderators only.
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="container-page py-14" data-testid="admin-certifications">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="chip inline-flex items-center gap-1.5">
            <ClipboardCheck size={14} /> Admin
          </span>
          <span className="chip inline-flex items-center gap-1.5 bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))]">
            <Award size={14} /> Certifications
          </span>
        </div>
        <h1 className="mt-4 font-serif text-4xl tracking-tight">Certification applications</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">
          Review beta reader submissions. You can approve, decline, or ask the applicant for more
          information when a sample or portfolio link isn&apos;t enough to evaluate their feedback
          quality. Approved readers get the Last Bookmark Certified badge on their profile and
          receive an in-app notification.
        </p>

        {stats && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-4">
            <StatTile label="Pending" value={stats.pending} tone="clay" testid="admin-cert-stat-pending" />
            <StatTile label="More info" value={stats.more_info_requested} tone="clay" testid="admin-cert-stat-more-info" />
            <StatTile label="Approved" value={stats.granted} tone="forest" testid="admin-cert-stat-granted" />
            <StatTile label="Rejected" value={stats.rejected} tone="muted" testid="admin-cert-stat-rejected" />
            <StatTile label="Total" value={stats.total} testid="admin-cert-stat-total" />
          </div>
        )}

        <div
          className="mt-8 inline-flex rounded-full border p-1 text-sm"
          data-testid="admin-cert-tabs"
        >
          {TABS.map((t) => {
            const active = tab === t.key;
            const count = stats
              ? t.key === "granted"
                ? stats.granted
                : t.key === "rejected"
                ? stats.rejected
                : t.key === "more_info_requested"
                ? stats.more_info_requested
                : stats.pending
              : null;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`admin-cert-tab-${t.key}`}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 transition ${
                  active
                    ? "bg-[hsl(var(--sb-forest))] text-white"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <t.icon size={14} /> {t.label}
                {count != null && (
                  <span className={`ml-1 text-xs ${active ? "opacity-80" : "opacity-60"}`}>
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-6 space-y-4">
          {loading && (
            <div className="text-sm text-muted-foreground" data-testid="admin-cert-loading">
              Loading applications…
            </div>
          )}
          {!loading && items.length === 0 && <EmptyState tab={tab} />}
          {!loading &&
            items.map((cert) => (
              <ApplicationCard key={cert.id} cert={cert} onDecide={decide} />
            ))}
        </div>
      </section>
    </SiteShell>
  );
}

function StatTile({ label, value, tone, testid }) {
  const toneClass =
    tone === "forest"
      ? "bg-[hsl(var(--sb-forest))]/8 border-[hsl(var(--sb-forest))]/25 text-[hsl(var(--sb-forest-2))]"
      : tone === "muted"
      ? "bg-muted border-border text-muted-foreground"
      : tone === "clay"
      ? "bg-[hsl(var(--sb-clay))]/8 border-[hsl(var(--sb-clay))]/25 text-[hsl(var(--sb-clay))]"
      : "bg-background border-border";
  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`} data-testid={testid}>
      <div className="text-[11px] uppercase tracking-[0.14em] opacity-75">{label}</div>
      <div className="mt-1 font-serif text-3xl">{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function EmptyState({ tab }) {
  return (
    <div className="card-soft text-center py-16" data-testid="admin-cert-empty">
      <div className="mx-auto w-14 h-14 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center">
        <ClipboardCheck size={22} className="text-[hsl(var(--sb-forest-2))]" />
      </div>
      <h3 className="mt-4 font-serif text-2xl">
        {tab === "pending"
          ? "Inbox zero — no pending applications."
          : tab === "more_info_requested"
          ? "No applicants waiting on more info."
          : tab === "granted"
          ? "No approved certifications yet."
          : "No rejected applications."}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
        {tab === "pending"
          ? "New submissions from beta readers will appear here for review."
          : tab === "more_info_requested"
          ? "When you request more information from an applicant, their application will move here until they respond."
          : tab === "granted"
          ? "Once you approve applicants, they'll be listed here as your certified community grows."
          : "Declined applications are archived here for reference and future reconsideration."}
      </p>
    </div>
  );
}

function ApplicationCard({ cert, onDecide }) {
  const [expanded, setExpanded] = useState(cert.status === "pending" || cert.status === "more_info_requested");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [askMessage, setAskMessage] = useState(
    "The portfolio link provided isn't taking us to your work. Please provide a working direct link or another sample of beta-reading feedback for review.",
  );
  const status = STATUS_STYLES[cert.status] || STATUS_STYLES.pending;
  const applicant = cert.applicant || {};
  const isActionable = cert.status === "pending" || cert.status === "more_info_requested";
  const infoRequests = cert.info_requests || [];
  const infoResponses = cert.info_responses || [];

  const submittedOn = cert.created_at ? new Date(cert.created_at).toLocaleString() : "—";
  const decidedOn = cert.granted_at || cert.rejected_at;

  const handle = async (action) => {
    setBusy(true);
    try {
      await onDecide(cert, action, { internal_notes: notes });
    } catch {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const handleAsk = async () => {
    if (askMessage.trim().length < 10) return;
    setBusy(true);
    try {
      await onDecide(cert, "request-info", { message: askMessage.trim(), internal_notes: notes });
      setAskOpen(false);
    } catch {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <article
      className="card-soft"
      data-testid={`admin-cert-card-${cert.id}`}
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <Avatar applicant={applicant} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-serif text-xl truncate" data-testid={`admin-cert-name-${cert.id}`}>
                {applicant.display_name || "Unknown applicant"}
              </h3>
              <span
                className={`text-[10px] uppercase tracking-[0.16em] rounded-full border px-2.5 py-0.5 ${status.className}`}
                data-testid={`admin-cert-status-${cert.id}`}
              >
                {status.label}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Mail size={12} />
              <span className="truncate">{applicant.email || "—"}</span>
            </div>
            {applicant.headline && (
              <div className="mt-0.5 text-xs text-muted-foreground truncate">
                {applicant.headline}
              </div>
            )}
          </div>
        </div>
        <div className="text-right text-xs text-muted-foreground shrink-0">
          <div>Submitted</div>
          <div className="font-medium text-foreground">{submittedOn}</div>
          {decidedOn && (
            <>
              <div className="mt-1">{cert.status === "granted" ? "Approved" : "Rejected"}</div>
              <div className="font-medium text-foreground">
                {new Date(decidedOn).toLocaleString()}
              </div>
            </>
          )}
        </div>
      </header>

      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mt-3 text-xs font-medium text-[hsl(var(--sb-forest-2))] hover:underline"
        data-testid={`admin-cert-toggle-${cert.id}`}
      >
        {expanded ? "Hide details" : "View full application"}
      </button>

      {expanded && (
        <div className="mt-5 space-y-5" data-testid={`admin-cert-details-${cert.id}`}>
          <Section
            icon={FileText}
            title="Statement"
            body={cert.statement || <em className="text-muted-foreground">Not provided</em>}
          />
          <Section
            icon={MessageSquare}
            title="Why they want to be certified"
            body={cert.motivation || <em className="text-muted-foreground">Not provided</em>}
          />
          <div className="grid sm:grid-cols-2 gap-4">
            <Section
              icon={BookOpen}
              title="Genres"
              body={
                (cert.genres || []).length ? (
                  <div className="flex flex-wrap gap-1.5">
                    {(cert.genres || []).map((g) => (
                      <span
                        key={g}
                        className="text-[11px] rounded-full border px-2 py-0.5 bg-muted/40"
                      >
                        {g}
                      </span>
                    ))}
                  </div>
                ) : (
                  <em className="text-muted-foreground">Not specified</em>
                )
              }
            />
            <Section
              icon={ClipboardCheck}
              title="Manuscripts beta read"
              body={
                cert.manuscripts_read ? (
                  MANUSCRIPT_LABELS[cert.manuscripts_read] || cert.manuscripts_read
                ) : (
                  <em className="text-muted-foreground">Not specified</em>
                )
              }
            />
          </div>
          <Section
            icon={ExternalLink}
            title="Sample feedback / portfolio link"
            body={
              cert.sample_link ? (
                <a
                  href={cert.sample_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 link-underline text-[hsl(var(--sb-forest-2))] break-all"
                  data-testid={`admin-cert-sample-link-${cert.id}`}
                >
                  {cert.sample_link} <ExternalLink size={12} />
                </a>
              ) : (
                <em className="text-muted-foreground">Not provided</em>
              )
            }
          />
          {cert.sample_text && (
            <Section
              icon={FileText}
              title="Pasted feedback sample"
              body={
                <div
                  className="rounded-lg border bg-muted/40 p-3 whitespace-pre-wrap text-sm leading-relaxed"
                  data-testid={`admin-cert-sample-text-${cert.id}`}
                >
                  {cert.sample_text}
                </div>
              }
            />
          )}
          {cert.portfolio_notes && (
            <Section icon={FileText} title="Portfolio notes" body={cert.portfolio_notes} />
          )}
          <div className="rounded-xl border bg-muted/30 p-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Community standards agreement:</span>{" "}
            {cert.agreement_accepted ? "Accepted" : "Not accepted"}
            {cert.agreement_accepted_at &&
              ` on ${new Date(cert.agreement_accepted_at).toLocaleString()}`}
          </div>
          {cert.internal_notes && (
            <div
              className="rounded-xl border border-[hsl(var(--sb-forest))]/25 bg-[hsl(var(--sb-forest))]/5 p-3 text-sm"
              data-testid={`admin-cert-internal-notes-${cert.id}`}
            >
              <div className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--sb-forest-2))]">
                Internal notes
              </div>
              <div className="mt-1 whitespace-pre-wrap">{cert.internal_notes}</div>
            </div>
          )}
          {applicant.id && (
            <Link
              to={`/profile/${applicant.id}`}
              className="text-xs link-underline inline-flex items-center gap-1"
              data-testid={`admin-cert-profile-link-${cert.id}`}
            >
              Open applicant profile <ExternalLink size={11} />
            </Link>
          )}

          {(infoRequests.length > 0 || infoResponses.length > 0) && (
            <div
              className="rounded-xl border border-[hsl(var(--sb-clay))]/25 bg-[hsl(var(--sb-clay))]/5 p-3.5 space-y-3"
              data-testid={`admin-cert-info-thread-${cert.id}`}
            >
              <div className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--sb-clay-2))] font-medium flex items-center gap-1.5">
                <MessagesSquare size={12} /> Info request thread
              </div>
              {(() => {
                const combined = [
                  ...infoRequests.map((r, i) => ({ kind: "request", data: r, key: `req-${i}` })),
                  ...infoResponses.map((r, i) => ({ kind: "response", data: r, key: `res-${i}` })),
                ].sort((a, b) => {
                  const ta = a.data.requested_at || a.data.submitted_at || "";
                  const tb = b.data.requested_at || b.data.submitted_at || "";
                  return ta.localeCompare(tb);
                });
                return combined.map((entry) => (
                  <div
                    key={entry.key}
                    className={`rounded-lg border p-3 text-sm ${
                      entry.kind === "request"
                        ? "bg-background border-border"
                        : "bg-[hsl(var(--sb-moss))]/40 border-[hsl(var(--sb-forest))]/25"
                    }`}
                  >
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      <span>{entry.kind === "request" ? "Admin asked" : "Applicant replied"}</span>
                      <span>
                        {new Date(
                          entry.data.requested_at || entry.data.submitted_at || Date.now(),
                        ).toLocaleString()}
                      </span>
                    </div>
                    <div className="mt-1.5 whitespace-pre-wrap leading-relaxed">
                      {entry.data.message}
                    </div>
                    {entry.kind === "response" && entry.data.sample_link && (
                      <a
                        href={entry.data.sample_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-2 inline-flex items-center gap-1 text-xs link-underline text-[hsl(var(--sb-forest-2))] break-all"
                      >
                        {entry.data.sample_link} <ExternalLink size={11} />
                      </a>
                    )}
                    {entry.kind === "response" && entry.data.sample_text && (
                      <div className="mt-2 rounded-lg border bg-background p-2.5 text-xs whitespace-pre-wrap leading-relaxed">
                        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground mb-1">Pasted sample</div>
                        {entry.data.sample_text}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          )}

          {isActionable && (
            <div className="pt-4 border-t space-y-3">
              <div>
                <label
                  htmlFor={`admin-cert-notes-${cert.id}`}
                  className="text-sm font-medium mb-1.5 block"
                >
                  Internal notes <span className="text-muted-foreground font-normal">(optional, admin-only)</span>
                </label>
                <textarea
                  id={`admin-cert-notes-${cert.id}`}
                  rows={3}
                  className="sb-input"
                  placeholder="Anything you want future reviewers to know — never shown to the applicant."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid={`admin-cert-notes-input-${cert.id}`}
                />
              </div>
              {cert.status === "pending" ? (
                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => handle("reject")}
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-destructive/40 text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-60"
                    data-testid={`admin-cert-reject-${cert.id}`}
                  >
                    <X size={14} /> Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => setAskOpen(true)}
                    disabled={busy}
                    className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium border border-[hsl(var(--sb-clay))]/50 text-[hsl(var(--sb-clay-2))] hover:bg-[hsl(var(--sb-clay))]/10 transition-colors disabled:opacity-60"
                    data-testid={`admin-cert-request-info-${cert.id}`}
                  >
                    <HelpCircle size={14} /> Request more information
                  </button>
                  <button
                    type="button"
                    onClick={() => handle("grant")}
                    disabled={busy}
                    className="btn-primary"
                    data-testid={`admin-cert-approve-${cert.id}`}
                  >
                    <Check size={14} /> {busy ? "Saving…" : "Approve & grant badge"}
                  </button>
                </div>
              ) : (
                <p
                  className="text-xs text-muted-foreground italic"
                  data-testid={`admin-cert-waiting-${cert.id}`}
                >
                  Waiting on the applicant to respond. When they do, this application will move back to the Pending queue.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {askOpen && (
        <RequestInfoModal
          certId={cert.id}
          message={askMessage}
          setMessage={setAskMessage}
          busy={busy}
          onCancel={() => setAskOpen(false)}
          onSubmit={handleAsk}
        />
      )}
    </article>
  );
}

function RequestInfoModal({ certId, message, setMessage, busy, onCancel, onSubmit }) {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      data-testid={`admin-cert-info-modal-${certId}`}
      onClick={onCancel}
    >
      <div
        className="w-full max-w-lg rounded-2xl border bg-background p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <HelpCircle size={18} className="text-[hsl(var(--sb-clay-2))]" />
          <h3 className="font-serif text-xl">Request more information</h3>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          Tell the applicant exactly what you need — a working link, a different sample, a page
          that actually shows their feedback, etc. They&apos;ll get an in-app notification and can
          reply without submitting a new application.
        </p>
        <label
          htmlFor={`admin-cert-info-message-${certId}`}
          className="mt-4 block text-sm font-medium mb-1.5"
        >
          Message to the applicant <span className="text-destructive">*</span>
        </label>
        <textarea
          id={`admin-cert-info-message-${certId}`}
          rows={6}
          className="sb-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          data-testid={`admin-cert-info-message-input-${certId}`}
          minLength={10}
          maxLength={4000}
        />
        <div className="mt-1 text-[11px] text-muted-foreground">
          {message.trim().length}/4000 · minimum 10 characters.
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-full px-4 py-2 text-sm border hover:bg-muted"
            data-testid={`admin-cert-info-cancel-${certId}`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={busy || message.trim().length < 10}
            className="btn-primary"
            data-testid={`admin-cert-info-send-${certId}`}
          >
            {busy ? "Sending…" : "Send request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
        <Icon size={12} /> {title}
      </div>
      <div className="mt-1.5 text-sm whitespace-pre-wrap leading-relaxed">{body}</div>
    </div>
  );
}

function Avatar({ applicant }) {
  const size = 44;
  const url = applicant?.avatar_url;
  const name = applicant?.display_name || applicant?.email || "?";
  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{ width: size, height: size }}
        className="rounded-full object-cover ring-2 ring-background"
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full grid place-items-center bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))] font-serif font-semibold ring-2 ring-background shrink-0"
    >
      {(name[0] || "?").toUpperCase()}
    </div>
  );
}
